import { Directive, ElementRef, HostBinding, HostListener, Input, Renderer2 } from '@angular/core';
import { TableDirective } from './table.directive';

/**
 * Marks a table row as interactive with selection, focus, and click handling support.
 *
 * @remarks
 * This directive must be applied to `<tr>` elements within a table that has the {@link TableDirective}.
 * It provides:
 * - **Row Selection**: Integrates with parent table's selection state via the `selected` property binding
 * - **Click Handling**: Propagates row clicks to the parent table for event emission via `rowClick`
 * - **Keyboard Focus Management**: Coordinates with {@link TableFocusDirective} for keyboard navigation
 *   by applying the 'active' CSS class during focus to enable visual feedback
 *
 * Each row can be selected/deselected, and the selection state is tracked by the parent {@link TableDirective}.
 *
 * @example
 *
 * ```html
 * <table [bbTable]="countries" bbTableFocus>
 *   <tbody>
 *     @for (country of countries; track country.id) {
 *       <tr [bbRow]="country">
 *         <td>{{ country.name }}</td>
 *         <td>{{ country.population }}</td>
 *       </tr>
 *     }
 *   </tbody>
 * </table>
 * ```
 */
@Directive({
  selector: 'tr[bbRow]',
  standalone: false,
})
export class TableRowDirective {
  /**
   * The data object associated with this table row.
   *
   * @remarks
   * This input receives the data item for the row. The same object reference is passed to the parent
   * {@link TableDirective} when the row is clicked or selected, enabling data-driven row interactions.
   * The parent table uses this reference to track selection state.
   *
   * @example
   *
   * ```html
   * <tr [bbRow]="countryData">
   *   <td>{{ countryData.name }}</td>
   * </tr>
   * ```
   */
  @Input() bbRow: any;

  /**
   * Indicates whether this row is currently selected.
   *
   * @remarks
   * This computed property reflects the selection state by checking if the row's data object
   * is tracked in the parent table's selection set. When `true`, the CSS class 'selected' is
   * automatically applied to the row element via {@link HostBinding}.
   *
   * The parent {@link TableDirective} manages the actual selection state through row click handlers
   * and checkbox interactions.
   *
   * @returns `true` if this row's data is in the parent table's selected rows set, `false` otherwise
   */
  @HostBinding('class.selected')
  get selected() {
    return this.parentTable.isSelected(this.bbRow);
  }

  constructor(
    private readonly parentTable: TableDirective,
    private readonly renderer: Renderer2,
  ) {}

  /**
   * Handles row clicks and emits the click event to the parent table.
   *
   * @remarks
   * When a user clicks on this row, the directive notifies the parent {@link TableDirective},
   * which emits a `rowClick` event with the row's data. This enables components to respond
   * to row selection/interaction events.
   *
   * If using keyboard navigation, the {@link TableFocusDirective} coordinates with this handler
   * through the 'active' class to trigger clicks on the focused row when the Enter key is pressed.
   */
  @HostListener('click')
  onRowClick() {
    this.parentTable.onRowClick(this.bbRow);
  }

  /**
   * Applies focus styling when the row receives keyboard focus.
   *
   * @remarks
   * When a row element receives focus (via keyboard navigation or programmatic focus),
   * this handler adds the 'active' CSS class to provide visual focus feedback. This is
   * coordinated with {@link TableFocusDirective} for keyboard navigation support.
   *
   * The 'active' class is used by {@link TableFocusDirective} to identify the currently
   * focused row when processing Enter key presses.
   */
  @HostListener('focus', ['$event.target'])
  onFocus(el: ElementRef) {
    this.renderer.addClass(el, 'active');
  }

  /**
   * Removes focus styling when the row loses keyboard focus.
   *
   * @remarks
   * When a row element loses focus (via keyboard navigation away or focus moving to another element),
   * this handler removes the 'active' CSS class to indicate the row is no longer focused.
   *
   * This is part of the focus management system that works with {@link TableFocusDirective}
   * to provide clear visual feedback during keyboard navigation.
   */
  @HostListener('blur', ['$event.target'])
  onBlur(el: ElementRef) {
    this.renderer.removeClass(el, 'active');
  }
}
